import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { clearToken, isRemembered } from '../utils/tokenStorage';

// Custom string storage that picks localStorage or sessionStorage based on remember_me flag.
// Wrapped with createJSONStorage so zustand handles (de)serialization correctly under v5.
const authStringStorage = {
  getItem: (name) => {
    // Check localStorage first (remember me), then sessionStorage
    const lsVal = localStorage.getItem(name);
    const ssVal = sessionStorage.getItem(name);
    return lsVal || ssVal || null;
  },
  setItem: (name, value) => {
    if (isRemembered()) {
      localStorage.setItem(name, value);
      sessionStorage.removeItem(name); // clean up the other one
    } else {
      sessionStorage.setItem(name, value);
      localStorage.removeItem(name); // clean up the other one
    }
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
};

export const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => set({ user, isAuthenticated: !!user, isLoading: false }),
      updateUser: (updates) => set((state) => ({
        user: state.user ? { ...state.user, ...updates } : updates,
        isAuthenticated: true,
        isLoading: false,
      })),
      setLoading: (isLoading) => set({ isLoading }),
      logout: () => {
        clearToken();
        localStorage.removeItem('remember_me');
        set({ user: null, isAuthenticated: false });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => authStringStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // Mark loading complete once persisted state has been rehydrated so
      // pages that gate on auth don't render a logged-out shell first.
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isLoading = false;
        }
      },
    }
  )
);
